import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const SB_URL  = import.meta.env.VITE_SUPABASE_URL  as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface MaterialEntry {
  id:                string;
  surface_type:      string;
  label:             string;
  albedo_url:        string | null;
  roughness_url:     string | null;
  metalness_url:     string | null;
  normal_url:        string | null;
  confidence:        number | null;
  source_product_id: string | null;
  usage_count:       number;
  created_at:        string;
  updated_at:        string;
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

function MapThumb({ url, label }: { url: string | null; label: string }) {
  if (!url) {
    return (
      <div className="flex items-center justify-center h-14 w-14 rounded-lg border border-gray-800 bg-gray-900 text-gray-700 text-xs">
        —
      </div>
    );
  }
  return (
    <div className="relative group">
      <img
        src={url}
        alt={label}
        className="h-14 w-14 rounded-lg border border-gray-800 object-cover"
      />
      <span className="absolute inset-0 flex items-end justify-center rounded-lg bg-black/50 opacity-0 group-hover:opacity-100 transition pb-1 text-[9px] text-gray-300 uppercase tracking-wider">
        {label}
      </span>
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: number | null }) {
  if (confidence == null) return <span className="text-gray-600 text-xs">—</span>;
  const pct = Math.round(confidence * 100);
  const cls = pct >= 90 ? "text-green-400" : pct >= 70 ? "text-yellow-400" : "text-red-400";
  return <span className={`text-xs font-medium tabular-nums ${cls}`}>{pct}%</span>;
}

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 space-y-3 animate-pulse">
      <div className="h-4 w-32 rounded bg-gray-800" />
      <div className="flex gap-2">
        {[1,2,3,4].map((i) => <div key={i} className="h-14 w-14 rounded-lg bg-gray-800" />)}
      </div>
      <div className="h-3 w-20 rounded bg-gray-800" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminMaterialLibrary() {
  const { session } = useAuth();
  const jwt = session?.access_token ?? "";

  const [entries, setEntries]   = useState<MaterialEntry[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchEntries = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/material_library?select=*&order=surface_type.asc,confidence.desc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
      );
      if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
      setEntries(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load material library");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [jwt]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  /* Group by surface_type — show one card per type, sorted by confidence */
  const grouped = entries.reduce<Record<string, MaterialEntry[]>>((acc, e) => {
    if (!acc[e.surface_type]) acc[e.surface_type] = [];
    acc[e.surface_type].push(e);
    return acc;
  }, {});
  const surfaceTypes = Object.keys(grouped).sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Material Library</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Reusable PBR profiles extracted by the pipeline (confidence ≥ 85%).
            Every new high-confidence product improves the baseline for its surface type.
          </p>
        </div>
        <button
          onClick={() => fetchEntries(true)}
          disabled={loading || refreshing}
          className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition shrink-0"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-xl border border-red-900/40 bg-red-900/20 px-4 py-3 text-sm text-red-400">
          {error}
          <button onClick={() => fetchEntries()} className="ml-3 underline hover:text-red-300">Retry</button>
        </div>
      )}

      {/* Empty state */}
      {!loading && surfaceTypes.length === 0 && !error && (
        <div className="rounded-xl border border-gray-800 py-16 text-center space-y-2">
          <p className="text-gray-400 text-sm">No materials in the library yet.</p>
          <p className="text-gray-600 text-xs">
            Materials are auto-added when a product is processed with PBR confidence ≥ 85%.
          </p>
        </div>
      )}

      {/* Surface type stats row */}
      {!loading && surfaceTypes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {surfaceTypes.map((st) => (
            <span key={st} className="rounded-full border border-gray-700 bg-gray-900 px-3 py-0.5 text-xs text-gray-400">
              {st.replace(/_/g, " ")}
              <span className="ml-1.5 text-gray-600">×{grouped[st].length}</span>
            </span>
          ))}
        </div>
      )}

      {/* Material cards grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : surfaceTypes.map((st) => {
              // Take best (highest confidence) entry per surface type for the preview
              const best = grouped[st][0];
              const totalUsage = grouped[st].reduce((sum, e) => sum + (e.usage_count ?? 1), 0);
              return (
                <div
                  key={st}
                  className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 space-y-3 hover:border-gray-700 transition"
                >
                  {/* Title row */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-200 capitalize">
                        {st.replace(/_/g, " ")}
                      </p>
                      <p className="text-xs text-gray-600">{best.label}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <ConfidenceBadge confidence={best.confidence} />
                      <p className="text-xs text-gray-600 mt-0.5">
                        {totalUsage} use{totalUsage !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  {/* Map thumbnails */}
                  <div className="flex gap-2">
                    <MapThumb url={best.albedo_url}    label="Albedo"    />
                    <MapThumb url={best.roughness_url} label="Roughness" />
                    <MapThumb url={best.metalness_url} label="Metalness" />
                    <MapThumb url={best.normal_url}    label="Normal"    />
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>
                      Updated {new Date(best.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                    {best.source_product_id && (
                      <Link
                        to={`/admin/products/${best.source_product_id}`}
                        className="text-orange-500 hover:text-orange-400 transition"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Source →
                      </Link>
                    )}
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
